import React from "react";
import { CheckIcon, LockIcon, MessagesSquareIcon, UserRoundCheckIcon, VideoIcon, BoxIcon } from "lucide-react";
import { foundingBenefits, foundingMemberSteps, foundingMembershipValue, foundingPerks, foundingPlans, FoundingMemberStepIcon } from "../../data/foundingOffer";
import { PlanPrice } from "./PlanPrice";
const stepIcons: Record<FoundingMemberStepIcon, BoxIcon> = {
  profile: UserRoundCheckIcon,
  onboarding: VideoIcon,
  community: MessagesSquareIcon,
  rate: LockIcon
};
export function FoundingOffer() {
  return <div className="space-y-20 md:space-y-24" data-id="element-536" data-mp="g1ihahc0g6dv">
      {/* What founding members get */}
      <section aria-labelledby="founding-benefits-heading" data-id="element-537" data-mp="b8fmy6zesswm">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-4" data-id="element-538" data-mp="6ae33ju5spk3">
          Your founding offer
        </p>
        <h2 id="founding-benefits-heading" className="font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-539" data-mp="13qtl67onqgw8">
          What you have — permanently.
        </h2>

        <div className="mt-10 grid gap-5 sm:grid-cols-2" data-id="element-540" data-mp="316yjqkpwdq4q">
          {foundingBenefits.map(benefit => <div key={benefit.title} className="rounded-2xl border border-purple-200 border-t-2 border-t-purple-600 bg-white px-6 py-5" data-id="element-541" data-mp="365jj6hxbs5s4">
              <h3 className="font-serif text-lg text-charcoal" data-id="element-542" data-mp="2rfved6pxqozn">
                {benefit.title}
              </h3>
              <p className="mt-2 text-sm text-charcoal/70 leading-relaxed" data-id="element-543" data-mp="2wef5rmnassze">
                {benefit.description}
              </p>
            </div>)}
        </div>

        <div className="mt-6 rounded-2xl border-l-2 border-purple-600 bg-purple-50 px-6 py-6" data-id="element-544" data-mp="2mibu9q6k3hzs">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-800 mb-4" data-id="element-545" data-mp="28ikg9pg1i1ls">
            What you keep — forever
          </p>
          <ul className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2" data-id="element-546" data-mp="2dhdujk35hwn0">
            {foundingPerks.map(perk => <li key={perk} className="flex items-start gap-2.5 text-sm text-charcoal/80 leading-relaxed" data-id="element-547" data-mp="6rh611a2ap1r">
                <CheckIcon aria-hidden="true" className="mt-0.5 w-4 h-4 shrink-0 text-purple-700" data-id="element-548" data-mp="bnoeb0pxkcmv" />
                {perk}
              </li>)}
          </ul>
        </div>
      </section>

      {/* Plans */}
      <section aria-labelledby="founding-plans-heading" data-id="element-549" data-mp="5bljzx2azaiv">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-4" data-id="element-550" data-mp="bgzlvy9xvnd">
          Choose your plan
        </p>
        <h2 id="founding-plans-heading" className="font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-551" data-mp="3ritl2n66wv93">
          Your founding rate — locked in.
        </h2>
        <p className="mt-4 max-w-2xl text-base text-charcoal/70 leading-relaxed" data-id="element-552" data-mp="o1bai7z19a4z">
          Two months free to start. Beginning Month 3, you're billed at your
          founding rate — locked in for life. For Bloom, Thrive, and Collective
          tiers, each practitioner on the plan must be individually verified
          before their profile is activated.
        </p>

        <div className="mt-10 grid gap-5 sm:grid-cols-2" data-id="element-553" data-mp="16ycmicr08aif">
          {foundingPlans.map(plan => <div key={plan.name} className={`relative flex flex-col rounded-2xl px-6 py-6 ${plan.highlighted ? 'border-2 border-purple-600 bg-purple-50' : 'border border-purple-200 bg-white'}`} data-id="element-554" data-mp="1f7s1s8zw4wcv">
              {plan.highlighted && <span className="absolute -top-3 left-6 rounded-full bg-purple-700 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white" data-id="element-555" data-mp="1o7x7mepfrmox">
                  Most popular
                </span>}
              <h3 className="font-serif text-2xl text-charcoal" data-id="element-556" data-mp="1y5ba1aeiyay8">{plan.name}</h3>
              <p className="mt-1 text-sm text-charcoal/60" data-id="element-557" data-mp="227g76j46lppm">{plan.seats}</p>
              <div className="mt-5" data-id="element-558" data-mp="26yod356aysy1">
                <PlanPrice price={plan.price} priceSuffix={plan.priceSuffix} data-id="element-559" data-mp="26zprnjmj1xki" />
              </div>
              <ul className="mt-5 space-y-2.5" data-id="element-560" data-mp="2bwpvdnvwmo8g">
                {plan.features.map(feature => <li key={feature} className="flex items-start gap-2.5 text-sm text-charcoal/80 leading-relaxed" data-id="element-561" data-mp="2gxdxp5pl2rhz">
                    <CheckIcon aria-hidden="true" className="mt-0.5 w-4 h-4 shrink-0 text-purple-700" data-id="element-562" data-mp="2lx0bbg9bq9rb" />
                    {feature}
                  </li>)}
              </ul>
              {plan.note && <p className="mt-5 pt-4 border-t border-purple-200 text-sm italic text-charcoal/60 mt-auto" data-id="element-563" data-mp="3dor2o6z3jryx">
                  {plan.note}
                </p>}
            </div>)}
        </div>
      </section>

      {/* Founding membership value */}
      <section aria-labelledby="founding-value-heading" data-id="element-564" data-mp="3neu6l27raqc1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-4" data-id="element-565" data-mp="2znohrxju8npl">
          {foundingMembershipValue.eyebrow}
        </p>
        <h2 id="founding-value-heading" className="font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-566" data-mp="2urhi2mi1c8g3">
          {foundingMembershipValue.heading}
        </h2>
        <p className="mt-4 max-w-2xl text-base text-charcoal/70 leading-relaxed" data-id="element-567" data-mp="34lzqg4yecs68">
          {foundingMembershipValue.intro}
        </p>

        <p className="mt-10 text-base font-medium text-charcoal" data-id="element-568" data-mp="o0rsnjco55km">
          {foundingMembershipValue.lead}
        </p>

        <ol className="mt-8" data-id="element-569" data-mp="rh4k7o3glc6s">
          {foundingMemberSteps.map((step, index) => {
          const Icon = stepIcons[step.icon];
          const isLast = index === foundingMemberSteps.length - 1;
          return <li key={step.title} className="relative grid grid-cols-[3rem_minmax(0,1fr)] gap-5 md:grid-cols-[3.5rem_minmax(0,1fr)] md:gap-8" data-id="element-570" data-mp="hj6surqayz9y" data-mpi={index}>
                {!isLast && <span aria-hidden="true" className="absolute left-6 md:left-7 top-14 bottom-2 w-px bg-purple-200" data-id="element-571" data-mp="mjuk9eig1i4p" />}

                <span className="relative flex h-12 w-12 md:h-14 md:w-14 items-center justify-center rounded-full border border-purple-200 bg-ivory" data-id="element-572" data-mp="1f6fm58wjpcmi">
                  <Icon aria-hidden="true" className="h-5 w-5 md:h-6 md:w-6 text-purple-700" data-id="element-573" data-mp="10grja38pu5wo" />
                </span>

                <div className={isLast ? 'pt-1' : 'pt-1 pb-10 md:pb-12'} data-id="element-574" data-mp="15et3irhvob9q">
                  <p className="flex items-center gap-3 font-serif italic text-sm text-purple-700" data-id="element-575" data-mp="1t55gigrzcy76">
                    <span data-id="element-576" data-mp="1xxowtli1y7fo">
                      <span className="sr-only" data-id="element-577" data-mp="1xy86yg1gnrj5">Step </span>
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    {step.optional && <span className="rounded-full border border-purple-300 px-2.5 py-0.5 font-sans not-italic text-[10px] font-semibold uppercase tracking-[0.1em] text-purple-800" data-id="element-578" data-mp="22uvo86q1jgij">
                        Optional
                      </span>}
                  </p>
                  <h3 className="mt-1 font-serif text-2xl md:text-[1.75rem] text-charcoal leading-snug" data-id="element-579" data-mp="1ou1g7p6wnhke">
                    {step.title}
                  </h3>
                  <p className="mt-3 max-w-xl text-base text-charcoal/70 leading-relaxed" data-id="element-580" data-mp="1k20546lh3wvy">
                    {step.description}
                  </p>
                </div>
              </li>;
        })}
        </ol>

        <div className="mt-10 rounded-2xl bg-purple-800 px-6 py-7 md:px-8" data-id="element-581" data-mp="1b0jvw26ea0tb">
          <h3 className="font-serif text-2xl text-white leading-snug" data-id="element-582" data-mp="1630h915sbm0u">
            {foundingMembershipValue.perksHeading}
          </h3>
          <p className="mt-2 max-w-2xl text-base text-purple-100 leading-relaxed" data-id="element-583" data-mp="3crsq227yc6af">
            {foundingMembershipValue.perksBody}
          </p>
        </div>
      </section>
    </div>;
}